import { CopyIcon } from 'lucide-react';
import { MAP_OPTIONS } from '@/entities';
import { useTranslation } from 'react-i18next';
import { useAddressViewDelivery } from './hooks';
import { Typography } from '@/shared/components';
import { cn, getYandexLang } from '@/shared/lib';
import { Checkbox, Input } from '@/shared/shadcn';
import { ControlButtons } from './ControlButtons';
import { YMaps, Map } from '@pbe/react-yandex-maps';
import { formatCoordinatesForInput } from '@/shared/lib';
import { useCopyToClipboard, useRTL } from '@/shared/hooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { selectIsMinimalistic, useAppSelector } from '@/app/store';
import { DeliverySuggester, useChangePersonalInfoDelivery } from '@/features';

export const CourierDelivery = ({
  isOrderAnotherPerson,
  onClose,
  noTopMargin,
}: {
  isOrderAnotherPerson: boolean;
  onClose: VoidFunction;
  noTopMargin?: boolean;
}) => {
  const isRTL = useRTL();
  const { i18n, t } = useTranslation();
  const { t: tAddress } = useTranslation('address');
  const [showSuggester, setShowSuggester] = useState(false);
  const handleCopyToClipboard = useCopyToClipboard();
  const isMinimalistic = useAppSelector(selectIsMinimalistic);

  const {
    delivery,
    onSetPointLabel,
    onSetDeliveryAddressAndCoords,
    onSetAreManualCoordinates,
    onSetDeliveryAddressComponents,
  } = useChangePersonalInfoDelivery(isOrderAnotherPerson);

  const [actualAddress, setActualAddress] = useState(delivery.address ?? '');
  const [deliveryInitial] = useState(delivery);
  const { areManualCoordinates } = delivery;

  const [mapError, setMapError] = useState('');

  const [inputedCoords, setInputedCoords] = useState(
    delivery.coords ? `${delivery.coords.lat}, ${delivery.coords.lng}` : '',
  );
  const [pointCoords, setPointCoords] = useState<number[] | undefined>(
    delivery.coords ? [delivery.coords.lat, delivery.coords.lng] : undefined,
  );

  const mapState = useMemo(
    () => ({ center: pointCoords || [58.010455, 56.229443], zoom: 15, controls: [] }),
    [pointCoords],
  );

  useEffect(() => {
    if (delivery.address) setActualAddress(delivery.address);
  }, [delivery.address]);

  const { mapRef, onMapClick, onLoadMap, changeFindButtonLoading } = useAddressViewDelivery({
    delivery,
    setMapError,
    actualAddress,
    inputedCoords,
    isOrderAnotherPerson,
    changePointCoords: setPointCoords,
    changeInputedCoords: setInputedCoords,
    changePointLable: onSetPointLabel,
    changeActualAddress: (address: string) => {
      setActualAddress(address);
      if (delivery.coords) onSetDeliveryAddressAndCoords({ address, coordinates: delivery.coords });
    },
  });

  const onSaveModal = useCallback(() => {
    if (
      (delivery.pointLabel === tAddress('map.addressNotFound') || actualAddress !== delivery.address) &&
      !areManualCoordinates
    ) {
      setMapError(tAddress('map.selectPointOnMap'));
      return;
    }
    onSetDeliveryAddressAndCoords({
      address: actualAddress,
      coordinates: pointCoords ? { lat: pointCoords[0], lng: pointCoords[1] } : undefined,
    });
    onSetAreManualCoordinates(areManualCoordinates);
    onClose();
  }, [
    onClose,
    tAddress,
    pointCoords,
    actualAddress,
    delivery.address,
    delivery.pointLabel,
    areManualCoordinates,
    onSetAreManualCoordinates,
    onSetDeliveryAddressAndCoords,
  ]);

  const onCancelModal = useCallback(() => {
    const { address, coords, areManualCoordinates, city, street, house } = deliveryInitial;
    onSetDeliveryAddressAndCoords({ address, coordinates: coords });
    onSetAreManualCoordinates(areManualCoordinates);
    onSetDeliveryAddressComponents({ city, street, house });
    onClose();
  }, [
    deliveryInitial,
    onClose,
    onSetAreManualCoordinates,
    onSetDeliveryAddressAndCoords,
    onSetDeliveryAddressComponents,
  ]);

  return (
    <div className='flex flex-col justify-between h-full'>
      <div className='flex flex-col gap-3 w-full h-full'>
        {!showSuggester && (
          <div className={cn('relative mt-4', noTopMargin && 'mt-0')}>
            <Typography
              variant='subtitle3'
              className={cn(
                'absolute top-[-6px] text-muted-foreground bg-background px-[2px] z-[2]',
                isRTL ? 'right-4' : 'left-4',
              )}
            >
              {t('details.address', { ns: 'orders' })}
            </Typography>

            <Input
              placeholder={tAddress('map.enterAddress')}
              value={actualAddress}
              disabled={areManualCoordinates}
              onChange={(e) => setActualAddress(e.target.value)}
              onMouseDown={(e) => {
                e.stopPropagation();
                setShowSuggester(true);
              }}
            />
          </div>
        )}
        {showSuggester && (
          <DeliverySuggester
            className={cn('z-10 mt-4', noTopMargin && 'mt-0')}
            onSuccess={() => setShowSuggester(false)}
            isOrderAnotherPerson={isOrderAnotherPerson}
            inputClassName={mapError && 'border-destructive'}
          />
        )}
        <div
          onClick={() => {
            onSetAreManualCoordinates(!areManualCoordinates);
            const coords = pointCoords;
            if (!coords) return;

            onSetDeliveryAddressAndCoords({
              address: actualAddress ?? '',
              coordinates: { lat: coords[0], lng: coords[1] + 0.0000001 },
            });
            setInputedCoords(
              formatCoordinatesForInput({
                latitude: coords[0],
                longitude: coords[1] + 0.000001,
              }),
            );
            if (delivery.pointLabel === tAddress('map.addressNotFound')) setActualAddress('');
          }}
          className='flex w-fit gap-2 cursor-pointer'
        >
          <Checkbox checked={areManualCoordinates} className='size-4' />
          <Typography variant='text3'>{tAddress('map.enterCoordinatesManually')}</Typography>
        </div>
        {areManualCoordinates && (
          <div className='relative'>
            <Typography
              variant='subtitle3'
              className={cn(
                'absolute top-[-6px] text-muted-foreground bg-background px-[2px] z-[2]',
                isRTL ? 'right-4' : 'left-4',
              )}
            >
              {tAddress('map.coordinates')}
            </Typography>

            <div className='relative'>
              <Input
                value={inputedCoords}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9\s.,]/g, '');
                  setInputedCoords(value);
                }}
                placeholder={tAddress('map.enterCoordinates')}
                className={isRTL ? 'pl-10' : 'pr-10'}
              />

              <button
                className={cn('absolute top-0 cursor-pointer p-3', isRTL ? 'left-0' : 'right-0')}
                onClick={() => handleCopyToClipboard(inputedCoords, t('info.coordsCopied', { ns: 'address' }))}
              >
                <CopyIcon size={16} className='text-muted-foreground' />
              </button>
            </div>
            <Typography variant='subtitle3' className='text-muted-foreground mt-1'>
              {tAddress('map.coordinatesWillBeSent')}
            </Typography>
          </div>
        )}

        <YMaps
          query={{
            lang: getYandexLang(i18n.language),
            load: 'package.full',
            apikey: import.meta.env.VITE_YANDEX_GEOCODER_API_KEY,
          }}
        >
          <Map
            modules={['Placemark', 'geocode', 'geoObject.addon.balloon', 'control.SearchControl']}
            instanceRef={mapRef}
            onLoad={(ympasInstance) => onLoadMap(ympasInstance)}
            onClick={onMapClick}
            state={mapState}
            options={MAP_OPTIONS}
            controls={['zoomControl', 'fullscreenControl']}
            className={cn('w-full h-full overflow-hidden', !isMinimalistic && 'rounded-md')}
          />
        </YMaps>
        {mapError ? <div className='text-sm mt-1 text-red-500'>{mapError}</div> : null}
      </div>

      <ControlButtons loading={changeFindButtonLoading} onSaveModal={onSaveModal} onCancelModal={onCancelModal} />
    </div>
  );
};
